/* File: src/components/game/hooks/useGameFlow.js */

import { useEffect, useCallback } from 'react';

/**
 * Manages post-game actions like retrying, starting the next session, or returning to the dashboard.
 * Also includes the effect to check for missed matches during gameplay.
 * Accepts a single dependency object from the calling component (GameScreen).
 */
const useGameFlow = ({
    // === Functions/State passed as dependencies from GameScreen ===

    // From useGameResponses:
    checkMissedMatches,         // Function to check for missed matches on trial change
    resetResponses,             // Function to clear UI feedback state

    // From GameContext (via useGame in GameScreen):
    resetContextResponseArrays, // Function to clear internal hit/miss arrays
    startGame,                  // Function to initiate a new game session
    sequence,                   // Current game sequence (for checkMissedMatches)
    currentTrial,               // Current trial index (for checkMissedMatches)
    gameState,                  // Current game state (for checkMissedMatches)
    gameResults,                // Results object after game ends (for retry logic AND challenge success check) // <<< Added comment
    sessionSettingsRef,         // Ref containing settings of the completed session

    // From useNavigate (passed from GameScreen):
    navigate,                   // React Router navigation function

    // From ProfileContext (via useProfile in GameScreen - optional, could be derived):
    activeProfile,              // Currently active profile (used for determining next level implicitly by startGame)

}) => {

    // --- Effect to Check for Missed Matches ---
    // Runs whenever the trial changes while the game might be playing.
    useEffect(() => {
        // Ensure checkMissedMatches was passed correctly and is a function
        if (typeof checkMissedMatches === 'function') {
            // The checkMissedMatches function itself should verify gameState === 'playing' internally
            // It checks the *previous* trial based on the new currentTrial value.
            checkMissedMatches(currentTrial, sequence, gameState);
        } else {
            // Log an error if the function is missing, helps debugging
            // This check might be redundant if GameScreen ensures it's passed, but safe to keep.
            if(gameState === 'playing') { // Only log error during active gameplay
                console.error("useGameFlow Effect Error: checkMissedMatches dependency is not a function!", { checkMissedMatches });
            }
        }
    // Dependencies: Effect reruns if these key values change
    }, [currentTrial, sequence, gameState, checkMissedMatches]);

    // --- Action Handlers (Wrapped with useCallback for stability) ---

    /**
     * Handler for the "Retry Level N.00" button in GameResults.
     * Resets necessary states and starts a new game session overriding N-Back and setting difficulty to 0.00.
     */
    const handleRetryLevel = useCallback(() => {
        console.log("[useGameFlow] handleRetryLevel triggered.");
        // Ensure we have results data to know which level to retry
        if (!gameResults || typeof gameResults.nBack !== 'number') {
            console.error("[useGameFlow] Cannot retry level: gameResults data or nBack is missing.", { gameResults });
            // Optionally navigate to dashboard as a fallback
            if (typeof navigate === 'function') navigate('/dashboard');
            return;
        }

        // 1. Reset UI feedback state (e.g., button highlights, feedback messages in useGameResponses)
        if (typeof resetResponses === 'function') {
            resetResponses();
        } else {
            console.warn("[useGameFlow] resetResponses function not provided for handleRetryLevel.");
        }

        // 2. Reset internal game context response arrays (visual/audio hits/misses stored in GameContext refs)
        if (typeof resetContextResponseArrays === 'function') {
            resetContextResponseArrays();
        } else {
            console.warn("[useGameFlow] resetContextResponseArrays function not provided for handleRetryLevel.");
        }

        // 3. Start the game with specific override options using the N-Back from the results
        if (typeof startGame === 'function') {
            const nBackToRetry = gameResults.nBack;
            console.log(`[useGameFlow] Calling startGame with override: N=${nBackToRetry}, Difficulty=0.00`);
            startGame({
                overrideNBack: nBackToRetry,
                overrideDifficulty: 0.00 // Retry always starts at the base difficulty for that N
            });
            // GameScreen's useEffect watching gameState will handle UI updates (e.g., showing countdown)
        } else {
            console.error("[useGameFlow] startGame action is not available for handleRetryLevel.");
            // Fallback navigation
            if (typeof navigate === 'function') navigate('/dashboard');
        }
    }, [gameResults, resetResponses, resetContextResponseArrays, startGame, navigate]); // Include all dependencies

    /**
     * Handler for the "Start Next Session" or "Play Again" button in GameResults.
     * Determines the appropriate next action based on whether the finished game
     * was a standard session, challenge, or playground session.
     * For standard sessions or *successful* challenges, it starts a new game using the current profile settings.
     * For *failed* challenges or playgrounds, it navigates back to the dashboard.
     */
    const handleStartNextSession = useCallback(() => {
        console.log("[useGameFlow] handleStartNextSession triggered.");

        // Check the type of the session that just ended using the sessionSettingsRef
        const wasChallengeAttempt = sessionSettingsRef?.current?.isChallengeAttempt;
        const wasPlayground = sessionSettingsRef?.current?.isPlayground;
        // *** Check if the challenge was successful using the flag from gameResults ***
        const challengeWasSuccessful = gameResults?.challengeSuccess === true;

        // Navigate to dashboard ONLY if it was playground OR a FAILED challenge attempt.
        // A successful challenge result should proceed to start the next game normally.
        if (wasPlayground || (wasChallengeAttempt && !challengeWasSuccessful)) {
            console.log(`[useGameFlow] Last session was ${wasPlayground ? 'Playground' : 'Failed Challenge'}. Navigating to dashboard.`);
            if (typeof navigate === 'function') {
                navigate('/dashboard');
            } else {
                console.error("[useGameFlow] navigate function not provided.");
            }
            return; // Stop execution here for these cases
        }

        // --- Logic for starting the *next standard* or *post-successful-challenge* training session ---

        // 1. Reset UI feedback state from the previous session
        if (typeof resetResponses === 'function') {
            resetResponses();
        } else {
            console.warn("[useGameFlow] resetResponses function not provided for handleStartNextSession.");
        }

        // 2. Reset internal game context response arrays
        if (typeof resetContextResponseArrays === 'function') {
            resetContextResponseArrays();
        } else {
            console.warn("[useGameFlow] resetContextResponseArrays function not provided for handleStartNextSession.");
        }

        // 3. Start a new standard game session (no overrides).
        // The `startGame` function in GameContext, when called without options,
        // should read the current N-Back and Difficulty from the active profile/context state.
        // This correctly handles starting Level N+1.00 after a successful challenge,
        // because completeChallengeLogic already updated the context state.
        if (typeof startGame === 'function') {
            console.log("[useGameFlow] Calling startGame for the next session (standard or post-successful-challenge)...");
            startGame(); // Call without options to use current context state
        } else {
            console.error("[useGameFlow] startGame action is not available for handleStartNextSession.");
             if (typeof navigate === 'function') navigate('/dashboard'); // Fallback
        }
    }, [
        resetResponses, resetContextResponseArrays, startGame, sessionSettingsRef, navigate, gameResults // <<< Added gameResults dependency
    ]);

    /**
     * Handler for the "Dashboard" button in GameResults.
     * Simply navigates the user back to the main dashboard screen.
     */
    const handleGoToDashboard = useCallback(() => {
        console.log("[useGameFlow] handleGoToDashboard triggered.");
        // No state reset needed here usually, as GameScreen unmount/remount should handle it,
        // but resetting UI feedback state might prevent brief flashes if navigation is slow.
        // if (typeof resetResponses === 'function') resetResponses();

        if (typeof navigate === 'function') {
            navigate('/dashboard');
        } else {
            console.error("[useGameFlow] navigate function not provided for handleGoToDashboard.");
        }
    }, [navigate]); // Dependency: navigate function

    // --- Return Hook Interface ---
    // Expose the handlers needed by the GameResults component (or potentially GameScreen)
    return {
        handleRetryLevel,
        handleStartNextSession,
        handleGoToDashboard
    };
};

export default useGameFlow; // Ensure it's the default export